import { Button, Select } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch } from "react-redux";
import InlineLoaderWithMessage from "../../../../components/inlineLoaderWithMessage/inlineLoaderWithMessage";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { handleGiConfigToggleStatus } from "../../../../components/modals/addNewViewTab/actionHandlers/handleGiConfigToggleStatus";
import AddNewViewTab from "../../../../components/modals/addNewViewTab/addNewViewTab";
import {
  ADD_NEW_VIEW,
  EDIT_VIEW_TAB_TYPE,
  GI_RPA_CONFIGURATION,
  LOADING_TEXT_FOR_HEALTH_CARE_FACILITIES,
  NO_GI_CONFIGURATIONS_FOUND,
  SELECT_A_HEALTH_CARE_FACILITY,
  TO_SYNC_CHANGES_IN_GI_CONFIGURATION_WITH_CHRON_JOB_CONFIGURATIONS,
} from "../../../../constants/stringLiterals";
import { ModalId } from "../../../../enums/modalId";
import { useHealthCareFacilities } from "../../../../hooks/useHealthCareFacilities";
import { useModalOpener } from "../../../../hooks/useModalOpener";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { InfoIcon } from "../../../../svg/info-icon";
import WarningIcon from "../../../../svg/warningIcon";
import { checkIfStringIsEmpty } from "../../../../utils/stringModifications";
import { handleAddNewViewTab } from "./actionHandlers/handleAddNewViewTab";
import GroupInboxRouteToggler from "./components/groupInboxRouteToggler";
import SelectHealthCareFacility from "./components/selectHealthCareFacility";
import ViewCard from "./components/viewCard";
import { useGiConfiguration } from "./hooks/useGiConfiguration";
import { generateGiConfigurationItemsData } from "./utils/generateGiConfigurationItemsData";

const ACTIVE_STATUS = "active";

/**
 * Group Inbox configuration page for a selected healthcare facility.
 *
 * Lists view-tab cards for the selected RPA/API route, persists status
 * toggles and add/edit saves with that route, and refreshes the list after
 * a successful save.
 *
 * @returns The GI configuration screen
 */
const GiRpaConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { healthCareFacilityOptions, healthCareFacilities } =
    useHealthCareFacilities();

  const {
    setSelectedHealthCareFacility,
    selectedGroupInboxRoute,
    setSelectedGroupInboxRoute,
    giConfigurationState,
    selectedHealthCareFacility,
    refreshGiConfigurations,
  } = useGiConfiguration();

  const { openedModalId } = useModalOpener();

  const checkIfGiCOnfigurationsAreEmpty = () => {
    return giConfigurationState.data?.length === 0;
  };

  return (
    <div className="options-configurations-layout flex h-full flex-col gap-4 overflow-hidden">
      <div className="options-configurations-layout__header flex items-center justify-between border-b border-gray-200 pb-2">
        <div className="options-configurations-layout__header text text-h11 font-semibold">
          {GI_RPA_CONFIGURATION}
        </div>
        <div className="options-configurations-layout__header action flex w-1/2 items-center justify-end gap-3">
          <GroupInboxRouteToggler
            selectedRoute={selectedGroupInboxRoute}
            onRouteChange={setSelectedGroupInboxRoute}
          />
          {healthCareFacilities.loading ? (
            <InlineLoaderWithMessage
              message={LOADING_TEXT_FOR_HEALTH_CARE_FACILITIES}
            />
          ) : (
            <div className="min-w-0 flex-1">
              <Select
                id="gi-configuration"
                options={[...healthCareFacilityOptions]}
                label=""
                placeholder={SELECT_A_HEALTH_CARE_FACILITY}
                onOptionChange={(option) => {
                  setSelectedHealthCareFacility(option.value);
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="options-configurations-layout__content flex flex-1 flex-col gap-4 overflow-hidden">
        {checkIfStringIsEmpty(selectedHealthCareFacility) && (
          <SelectHealthCareFacility />
        )}
        {giConfigurationState.loading ? (
          <>
            <div className="flex h-full w-full items-center justify-center">
              <LoaderMessage message="Loading GI configuration..." />
            </div>
          </>
        ) : (
          <>
            <div className="options-configurations-layout__content action-btn flex items-center justify-between gap-2">
              {!checkIfStringIsEmpty(selectedHealthCareFacility) && (
                <>
                  <div className="warning-banner flex flex-1 items-center gap-2 rounded-md border border-secondaryYellow-3 bg-secondaryYellow-11 p-2">
                    <WarningIcon height="24" width="24" />
                    <span className="text-sm font-semibold">
                      {
                        TO_SYNC_CHANGES_IN_GI_CONFIGURATION_WITH_CHRON_JOB_CONFIGURATIONS
                      }
                    </span>
                  </div>
                  <Button
                    disabled={false}
                    children={ADD_NEW_VIEW}
                    onClick={() => handleAddNewViewTab(dispatch)}
                    buttonType={"primary"}
                    size={"medium"}
                  />
                </>
              )}
            </div>
            <div className="options-configurations-layout__content content flex flex-1 flex-wrap content-start justify-between gap-y-4 overflow-auto">
              {checkIfGiCOnfigurationsAreEmpty() ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3">
                  <InfoIcon height="32" width="32" stroke="#0056D6" />
                  <span className="text-sm font-semibold">
                    {NO_GI_CONFIGURATIONS_FOUND}
                  </span>
                </div>
              ) : (
                <>
                  {!checkIfStringIsEmpty(selectedHealthCareFacility) &&
                    giConfigurationState.data?.map((item) => (
                      <ViewCard
                        id={item.id}
                        key={item.id}
                        title={item.view_tab || ""}
                        items={generateGiConfigurationItemsData(item)}
                        lastUpdated={item.last_updated}
                        onEdit={() => {
                          dispatch(
                            setOpenedModalId({
                              id: ModalId.ADD_NEW_VIEW_TAB,
                              metaData: {
                                type: EDIT_VIEW_TAB_TYPE,
                                selectedGiConfiguration: item,
                              },
                            }),
                          );
                        }}
                        isActive={item?.status === ACTIVE_STATUS}
                        onToggleSwitchChange={(data) =>
                          handleGiConfigToggleStatus(
                            data.value,
                            item.id,
                            selectedHealthCareFacility,
                            item.view_tab || "",
                            selectedGroupInboxRoute,
                          )
                        }
                      />
                    ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
      {openedModalId === ModalId.ADD_NEW_VIEW_TAB && (
        <AddNewViewTab
          giConfigurations={giConfigurationState.data ?? null}
          onClose={() => dispatch(closeModal())}
          onConfigSaved={refreshGiConfigurations}
          org_id={selectedHealthCareFacility}
          route={selectedGroupInboxRoute}
        />
      )}
    </div>
  );
};

export default GiRpaConfiguration;
